import React from 'react'
import styled from 'styled-components'
import onClickOutside from 'react-onclickoutside'

import { Text } from '../topography'
import Icon, { ArrowIcon } from '../icon/Icon'
import { translate } from '../../utils/translation'
import { palette, fonts, mediaQueries, shadows } from '../../styles/variables'
import ubersuggestIcon from '../../styles/assets/logo_ubersuggest.svg'
import aiWriterIcon from '../../styles/assets/logo_ai_writer.svg'
import circlCheckIcon from '../../styles/assets/circle_check_orange_fill.svg'
import { Desktop, SmallDesktop } from '../../utils/media'
import { AIWRITER_BASE_URL } from '../../utils/consts.mjs'
import {
  KISSMETRICS_TRACK_EVENTS,
  kissmetricsRecordingEvent,
} from '../../utils/kissmetrics'
import { waitFor } from '../../utils/api'

const uberApplications = [
  {
    id: 'ubersuggest',
    icon: ubersuggestIcon,
    title: 'ubersuggest',
    heading: 'empty_state_kw_overview_desp_1',
    bgColor: palette.orange10,
  },
  {
    id: 'ai_writer',
    icon: aiWriterIcon,
    title: 'ai_writer',
    heading: 'switcher_ai_writer_subheading',
    bgColor: palette.blue10,
    baseUrl: AIWRITER_BASE_URL,
  },
]

const AppSelectorWrapper = styled.div((props) => ({
  position: 'relative',
  marginRight: '30px',
  marginLeft: '30px',
  cursor: 'pointer',
  zIndex: 104,
  background: palette.white,
  [mediaQueries.smallDesktop]: {
    fontSize: '20px',
    margin: 0,
  },
  ...props.overridingStyle,
}))

const SelectedApp = styled.div((props) => ({
  display: 'flex',
  alignItems: 'center',
  minWidth: '228px',
  height: '40px',
  border: props.open ? `1px solid ${palette.darkGray20}` : 'none',
  borderRadius: '2px',
  padding: '0 16px 0 8px',
  justifyContent: 'space-between',
  backgroundColor: props.open ? palette.lightGray50 : palette.lightGray20,
  userSelect: 'none',
  '& div': {
    cursor: 'pointer',
  },
  '&:hover': {
    background: palette.lightGray30,
  },
  [mediaQueries.smallDesktop]: {
    minWidth: '100%',
  },
}))

const AppWrapper = styled.div((props) => ({
  display: 'flex',
  alignItems: 'flex-start',
  padding: '8px 16px',
  minWidth: '224px',
  borderRadius: '2px',
  cursor: 'pointer',
  '& div': {
    cursor: 'pointer',
  },
  '&:hover': {
    background: props.bgColor || palette.lightGray20,
  },
  [mediaQueries.smallDesktop]: {
    minWidth: '100%',
  },
}))

const AppList = styled.div(() => ({
  position: 'absolute',
  zIndex: 104,
  background: palette.white,
  boxShadow: shadows.shadowA,
  width: 'auto',
  minWidth: '273px',
  marginTop: '8px',
  padding: '8px',
  [mediaQueries.smallDesktop]: {
    minWidth: '100%',
  },
}))

const UberApp = ({ onClick, isSelected, bgColor, icon, heading, name }) => (
  <AppWrapper onClick={onClick} isSelected={isSelected} bgColor={bgColor}>
    <Icon src={icon} size="32px" margin="0 8px 0 0" />
    <div style={{ flex: 1 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
        }}
      >
        <Text
          fontSize="16px"
          lineHeight="16px"
          fontFamily={palette.primaryBook}
          whiteSpace="nowrap"
        >
          {translate(name)}
        </Text>
        {isSelected && (
          <Icon src={circlCheckIcon} size="16px" margin="0 0 0 8px" />
        )}
      </div>
      <Text
        fontSize="12px"
        lineHeight="20px"
        margin="8px 0 0 0"
        color={palette.darkGray50}
      >
        {translate(heading)}
      </Text>
    </div>
  </AppWrapper>
)

class AppSelector extends React.PureComponent {
  constructor(props) {
    super(props)

    this.state = {
      isOpen: false,
      selectedAppIndex: 0,
    }
  }
  // eslint-disable-next-line
  handleClickOutside = () => {
    this.setState({ isOpen: false })
  }

  openList = () => {
    this.setState((prevState) => ({ isOpen: !prevState.isOpen }))
  }

  selectApp = (index) => {
    if (this.state.selectedAppIndex !== index)
      this.setState({ selectedAppIndex: index }, () => {
        if (uberApplications[index].baseUrl) {
          kissmetricsRecordingEvent(KISSMETRICS_TRACK_EVENTS.switch_app, {
            switcher_button: 'AIWriter',
          })

          waitFor(500).then(() => {
            window.location.href = uberApplications[index].baseUrl
          })
        }
      })
    this.openList()
  }

  render() {
    const { isOpen, selectedAppIndex } = this.state
    const { style } = this.props
    return (
      <AppSelectorWrapper overridingStyle={style} data-testid="appSelector">
        <SelectedApp onClick={this.openList} open={isOpen}>
          <div
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
            }}
          >
            <Icon
              src={uberApplications[selectedAppIndex].icon}
              size="32px"
              margin="0 12px 0 0"
            />
            <Text
              lineHeight="16px"
              fontFamily={fonts.primaryBook}
              whiteSpace="nowrap"
              mobileWhiteSpace="nowrap"
            >
              {translate(uberApplications[selectedAppIndex].title)}
            </Text>
          </div>

          {isOpen ? (
            <>
              <Desktop minWidth={1281}>
                <ArrowIcon
                  down
                  color={palette.darkGray}
                  style={{ marginRight: '10px' }}
                />
              </Desktop>
              <SmallDesktop>
                <ArrowIcon
                  up
                  color={palette.darkGray}
                  style={{ marginRight: '10px' }}
                />
              </SmallDesktop>
            </>
          ) : (
            <>
              <Desktop minWidth={1281}>
                <ArrowIcon
                  right
                  color={palette.darkGray}
                  style={{ marginRight: '10px' }}
                />
              </Desktop>
              <SmallDesktop>
                <ArrowIcon
                  down
                  color={palette.darkGray}
                  style={{ marginRight: '10px' }}
                />
              </SmallDesktop>
            </>
          )}
        </SelectedApp>
        {isOpen && (
          <AppList>
            {uberApplications.map((app, index) =>
              index === selectedAppIndex ? null : (
                <UberApp
                  isSelected={index === selectedAppIndex}
                  key={app.id}
                  name={app.title}
                  heading={app.heading}
                  icon={app.icon}
                  bgColor={app.bgColor}
                  onClick={() => this.selectApp(index)}
                />
              ),
            )}
          </AppList>
        )}
      </AppSelectorWrapper>
    )
  }
}

export default onClickOutside(AppSelector)
